import React from "react";
import { useTranslation } from "react-i18next";
import { Profile } from "../../../types/types";
import { formatAlltext, formatText } from "../../../utils/common/formatUtils";

interface FightHistoryProps {
    member: Profile;
}

const FightHistory: React.FC<FightHistoryProps> = ({ member }) => {
    const { t } = useTranslation('publicProfile');
    if (!member.fighter) return null;

    const getResultColor = (res?: string | null) => {
        if (!res) return "text-gray-800";
        const lower = String(res).toLowerCase();
        if (lower.includes("win")) return "text-green-600 font-medium";
        if (lower.includes("loss") || lower.includes("lose")) return "text-red-600 font-medium";
        return "text-gray-800 font-medium";
    };

    return (
        <div className="space-y-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            <h3 className="text-sm font-medium text-gray-900 text-start">
                {t("fight_history")}
            </h3>
            <div>
                {member.fighter.fightHistories?.length ? (
                    <>
                        <div className="hidden md:block overflow-x-auto rounded-2xl border border-gray-100 bg-white">
                            <table className="w-full border-collapse text-start">
                                <thead>
                                    <tr className="bg-gray-50/80 border-b border-gray-100">
                                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 whitespace-nowrap">
                                            {t("opponent")}
                                        </th>
                                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 whitespace-nowrap">
                                            {t("date")}
                                        </th>
                                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 whitespace-nowrap">
                                            {t("result")}
                                        </th>
                                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 whitespace-nowrap">
                                            {t("round")}
                                        </th>
                                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 whitespace-nowrap">
                                            {t("location")}
                                        </th>
                                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 whitespace-nowrap">
                                            {t("organization")}
                                        </th>
                                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 whitespace-nowrap">
                                            {t("type_of_ending")}
                                        </th>
                                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 whitespace-nowrap">
                                            {t("rule_set")}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {member.fighter.fightHistories.map((fight: any) => (
                                        <tr key={fight.id} className="hover:bg-gray-50/60 transition-colors">
                                            <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                                                {formatText(fight.opponentName)}
                                            </td>
                                            <td className="px-4 py-3 text-sm font-normal text-gray-600 whitespace-nowrap">
                                                {new Date(fight.date).toLocaleDateString()}
                                            </td>
                                            <td className={`px-4 py-3 text-sm whitespace-nowrap ${getResultColor(fight.result)}`}>
                                                {formatText(fight.result)}
                                            </td>
                                            <td className="px-4 py-3 text-sm font-normal text-gray-700 whitespace-nowrap">
                                                {formatText(fight.round)}
                                            </td>
                                            <td className="px-4 py-3 text-sm font-normal text-gray-700 whitespace-nowrap">
                                                {formatText(fight.location)}
                                            </td>
                                            <td className="px-4 py-3 text-sm font-normal text-gray-700 whitespace-nowrap">
                                                {formatText(fight.organizationName)}
                                            </td>
                                            <td className="px-4 py-3 text-sm font-normal text-gray-700 whitespace-nowrap">
                                                {formatAlltext(fight.typeOfEnding?.toString())}
                                            </td>
                                            <td className="px-4 py-3 text-sm font-normal text-gray-700 whitespace-nowrap">
                                                {formatAlltext(fight.preferredRuleSet?.toString())}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="md:hidden flex flex-col gap-3">
                            {member.fighter.fightHistories.map((fight: any) => (
                                <div key={fight.id} className="rounded-2xl border border-gray-100 bg-white p-3 space-y-2 shadow-sm">
                                    <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                                        <div className="flex flex-col">
                                            <span className="text-xs text-gray-400 font-medium">{t("opponent")}</span>
                                            <span className="text-base font-bold text-gray-900">{formatText(fight.opponentName)}</span>
                                        </div>
                                        <span className={`text-sm ${getResultColor(fight.result)}`}>
                                            {formatText(fight.result)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs py-1 border-b border-gray-50">
                                        <span className="text-gray-400 font-medium">{t("date")}</span>
                                        <span className="text-gray-900 font-medium">{new Date(fight.date).toLocaleDateString()}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs py-1 border-b border-gray-50">
                                        <span className="text-gray-400 font-medium">{t("round")}</span>
                                        <span className="text-gray-700">{formatText(fight.round)}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs py-1 border-b border-gray-50">
                                        <span className="text-gray-400 font-medium">{t("location")}</span>
                                        <span className="text-gray-700">{formatText(fight.location)}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs py-1 border-b border-gray-50">
                                        <span className="text-gray-400 font-medium">{t("organization")}</span>
                                        <span className="text-gray-700">{formatText(fight.organizationName)}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs py-1 border-b border-gray-50">
                                        <span className="text-gray-400 font-medium">{t("type_of_ending")}</span>
                                        <span className="text-gray-700">{formatAlltext(fight.typeOfEnding?.toString())}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs pt-1">
                                        <span className="text-gray-400 font-medium">{t("rule_set")}</span>
                                        <span className="text-gray-700">{formatAlltext(fight.preferredRuleSet?.toString())}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                ) : (
                    <p className="text-gray-400 text-xs text-center py-8">
                        {t("no_fight_history")}
                    </p>
                )}
            </div>
        </div>
    );
};

export default FightHistory;
